import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { productsAPI, manufacturingAPI, aiAPI } from '@/utils/api';
import { Plus, Trash2, CheckCircle2, AlertTriangle, Sparkles, Loader2, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import DipBreadcrumb from '@/components/DipBreadcrumb';
import DipStepFooter from '@/components/DipStepFooter';

const Manufacturing = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [manufacturing, setManufacturing] = useState(null);
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState(['']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiReviewing, setAiReviewing] = useState(false);
  const [aiReview, setAiReview] = useState(null);
  const [aiRewriting, setAiRewriting] = useState(false);

  useEffect(() => {
    loadData();
  }, [productId]);

  const loadData = async () => {
    try {
      const prodRes = await productsAPI.getOne(productId);
      setProduct(prodRes.data);

      try {
        const manufRes = await manufacturingAPI.getByProduct(productId);
        setManufacturing(manufRes.data);
        setDescription(manufRes.data.description);
        setSteps(manufRes.data.steps);
      } catch (error) {
        console.log('No existing manufacturing process');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const addStep = () => {
    setSteps([...steps, '']);
  };

  const removeStep = (index) => {
    setSteps(steps.filter((_, i) => i !== index));
  };

  const updateStep = (index, value) => {
    const updated = [...steps];
    updated[index] = value;
    setSteps(updated);
  };

  const handleSave = async () => {
    const validSteps = steps.filter((s) => s.trim());

    if (!description.trim() || validSteps.length === 0) {
      toast.error('Remplissez la description et au moins une étape');
      return;
    }

    setSaving(true);
    try {
      const data = {
        product_id: productId,
        description,
        steps: validSteps,
      };

      if (manufacturing) {
        await manufacturingAPI.update(manufacturing.id, data);
        toast.success('Méthode mise à jour');
      } else {
        await manufacturingAPI.create(data);
        toast.success('Méthode créée avec succès');
      }

      loadData();
    } catch (error) {
      console.error('Error saving manufacturing:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleAIReview = async () => {
    if (!manufacturing) {
      toast.error('Enregistrez d\'abord la méthode de fabrication');
      return;
    }

    setAiReviewing(true);
    try {
      const res = await manufacturingAPI.aiReview(manufacturing.id);
      setAiReview(res.data);
      toast.success('Revue IA terminée');
      loadData();
    } catch (error) {
      console.error('AI Review Error:', error);
      toast.error('Erreur lors de la revue IA');
    } finally {
      setAiReviewing(false);
    }
  };

  const handleAIRewrite = async () => {
    if (!description.trim() && steps.filter(s => s.trim()).length === 0) {
      toast.error('Ajoutez d\'abord une description et des étapes');
      return;
    }
    setAiRewriting(true);
    try {
      const res = await aiAPI.rewriteBpf({ description, steps: steps.filter(s => s.trim()) });
      if (res.data.description) setDescription(res.data.description);
      if (res.data.steps && res.data.steps.length > 0) setSteps(res.data.steps);
      if (res.data.explanation) {
        toast.success('Méthode réécrite par l\'IA');
        toast.info(res.data.explanation, { duration: 8000 });
      } else {
        toast.success('Méthode réécrite par l\'IA');
      }
    } catch (error) {
      toast.error('Erreur lors de la réécriture IA');
    } finally {
      setAiRewriting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="skeleton-shimmer h-64 rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <DipBreadcrumb productId={productId} productName={product?.name} currentStep="manufacturing" />

      <div className="mb-8">
        <h1 className="text-4xl font-semibold text-foreground mb-2 tracking-tight">
          Méthode de fabrication
        </h1>
        <p className="text-base text-muted-foreground leading-relaxed">
          {product?.name} - Procédé de fabrication BPF (ISO 22716)
        </p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Description du procédé</CardTitle>
          <CardDescription>Décrivez la méthode de fabrication globale</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            data-testid="input-description"
            placeholder="Décrivez le procédé de fabrication en détail..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="min-h-[120px] resize-none"
          />
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Étapes de fabrication</CardTitle>
          <CardDescription>Listez les étapes dans l'ordre chronologique</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {steps.map((step, index) => (
            <div key={index} className="flex gap-3 items-start">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-medium text-sm">
                {index + 1}
              </div>
              <Input
                data-testid={`input-step-${index}`}
                placeholder={`Étape ${index + 1}`}
                value={step}
                onChange={(e) => updateStep(index, e.target.value)}
                className="flex-1"
              />
              {steps.length > 1 && (
                <Button
                  data-testid={`btn-remove-step-${index}`}
                  variant="ghost"
                  size="icon"
                  onClick={() => removeStep(index)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 size={18} />
                </Button>
              )}
            </div>
          ))}

          <Button
            data-testid="btn-add-step"
            variant="outline"
            onClick={addStep}
            className="w-full gap-2 mt-4"
          >
            <Plus size={18} />
            Ajouter une étape
          </Button>

          <Button
            data-testid="btn-ai-rewrite"
            variant="outline"
            onClick={handleAIRewrite}
            disabled={aiRewriting}
            className="w-full gap-2 mt-2 border-primary/30 text-primary hover:bg-primary/5"
          >
            {aiRewriting ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                Réécriture IA en cours...
              </>
            ) : (
              <>
                <Wand2 size={18} />
                Réécrire avec l'IA (BPF conforme)
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {manufacturing && (
        <Card className="mb-6">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="text-accent" size={24} />
                  Revue IA - Conformité BPF
                </CardTitle>
                <CardDescription>Vérification par l'assistant IA</CardDescription>
              </div>
              <Button
                data-testid="btn-ai-review"
                onClick={handleAIReview}
                disabled={aiReviewing}
                variant="outline"
                className="gap-2"
              >
                {aiReviewing ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Analyse...
                  </>
                ) : (
                  <>
                    <Sparkles size={18} />
                    Lancer la revue IA
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          {(manufacturing.ai_reviewed || aiReview) && (
            <CardContent>
              <div className="p-4 bg-muted rounded-md space-y-4">
                {aiReview?.review ? (
                  <>
                    <div className={`flex items-center gap-2 ${aiReview.review.compliant ? 'text-green-600' : 'text-red-600'}`}>
                      {aiReview.review.compliant ? <CheckCircle2 size={20} /> : <AlertTriangle size={20} />}
                      <span className="font-medium">
                        {aiReview.review.compliant ? 'Conforme aux BPF ISO 22716' : 'Non conforme aux BPF ISO 22716'}
                      </span>
                      {aiReview.review.score !== undefined && (
                        <span className="ml-2 text-sm text-muted-foreground">Score: {aiReview.review.score}/100</span>
                      )}
                    </div>
                    {aiReview.review.summary && (
                      <p className="text-sm text-foreground">{aiReview.review.summary}</p>
                    )}
                    {aiReview.review.issues?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-red-600 mb-2">Points non conformes</p>
                        <ul className="list-disc list-inside space-y-1">
                          {aiReview.review.issues.map((issue, i) => (
                            <li key={i} className="text-sm text-foreground">{issue}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {aiReview.review.recommendations?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-amber-600 mb-2">Recommandations</p>
                        <ul className="list-disc list-inside space-y-1">
                          {aiReview.review.recommendations.map((rec, i) => (
                            <li key={i} className="text-sm text-foreground">{rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {manufacturing.bpf_compliant ? (
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle2 size={20} />
                        <span className="font-medium">Conforme aux BPF ISO 22716</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-red-600">
                        <AlertTriangle size={20} />
                        <span className="font-medium">Non conforme aux BPF ISO 22716</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      <div className="flex justify-end gap-3">
        <Button variant="ghost" onClick={() => navigate('/products')}>
          Annuler
        </Button>
        <Button data-testid="btn-save-manufacturing" onClick={handleSave} disabled={saving}>
          {saving ? 'Enregistrement...' : 'Enregistrer la méthode'}
        </Button>
      </div>

      <DipStepFooter productId={productId} currentStep="manufacturing" />
    </div>
  );
};

export default Manufacturing;
